import type { RefundRecommendationResult } from 'store/patients/invoices/refundRecommendations.types';

const currencyInputPattern = /^\$?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/;
const utcCalendarDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;

const isLeapYear = (year: number): boolean =>
  year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

export const getUtcToday = (now: Date = new Date()): string => {
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Validates a last-active-day calendar value without parsing it through the local timezone.
 * Refund cancellation dates must represent a real UTC calendar date and cannot be in the future.
 */
export const isValidRefundCancellationDate = (value: string, now: Date = new Date()): boolean => {
  const dateParts = utcCalendarDatePattern.exec(value);

  if (!dateParts) {
    return false;
  }

  const day = Number(dateParts[3]);
  const month = Number(dateParts[2]);
  const year = Number(dateParts[1]);
  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  return (
    year > 0 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth[month - 1] &&
    value <= getUtcToday(now)
  );
};

export const parseCurrencyInputToCents = (value: string): number | null => {
  const trimmedValue = value.trim();

  if (!currencyInputPattern.test(trimmedValue)) {
    return null;
  }

  const valueWithoutSymbol = trimmedValue.startsWith('$') ? trimmedValue.slice(1) : trimmedValue;
  const [wholePart, fractionPart = ''] = valueWithoutSymbol.split('.');
  const centsDigits = `${wholePart.replace(/,/g, '')}${fractionPart.padEnd(2, '0')}`;
  const cents = Number(BigInt(centsDigits));

  return Number.isSafeInteger(cents) ? cents : null;
};

export const getOverrideDifferenceCents = (
  enteredAmount: string,
  recommendedAmountCents: number | null
): number | null => {
  if (recommendedAmountCents === null) {
    return null;
  }

  const enteredAmountCents = parseCurrencyInputToCents(enteredAmount);

  return enteredAmountCents === null ? null : enteredAmountCents - recommendedAmountCents;
};

export const resolveRefundableMaximumCents = (
  recommendation: RefundRecommendationResult | null | undefined,
  legacyFallbackCents?: number
): number | undefined => {
  if (recommendation?.outcome === 'available' || recommendation?.outcome === 'not-eligible') {
    return recommendation.breakdown.maxioRefundableCents;
  }

  return (
    recommendation?.unavailableSnapshot?.maxioRefundableCents ??
    recommendation?.breakdown?.maxioRefundableCents ??
    legacyFallbackCents
  );
};

/**
 * Converts backend and parsed integer cents into the decimal string accepted by refund APIs.
 * Keeping this at the shared boundary prevents the two refund forms from formatting wire amounts
 * differently while leaving all financial calculations on the backend.
 */
export const formatCentsAsDecimal = (amountCents: number): string => {
  const dollars = Math.floor(amountCents / 100);
  const cents = String(amountCents % 100).padStart(2, '0');

  return `${dollars}.${cents}`;
};

export const formatCentsAsCurrency = (amountCents: number): string => {
  const sign = amountCents < 0 ? '-' : '';
  const absoluteAmountCents = Math.abs(amountCents);
  const wholeDollars = Math.floor(absoluteAmountCents / 100);
  const cents = String(absoluteAmountCents % 100).padStart(2, '0');
  const formattedDollars = new Intl.NumberFormat('en-US').format(wholeDollars);

  return `${sign}$${formattedDollars}.${cents}`;
};

export const formatSignedCurrency = (amountCents: number): string => {
  const formattedAmount = formatCentsAsCurrency(amountCents);

  return amountCents > 0 ? `+${formattedAmount}` : formattedAmount;
};
